export const WEBVIEW_QUERY_KEY = "webviewQueryParams";

/**
 * Toggle for preserving all query params across auth redirects.
 * Set to false at call sites to restore original ?webview=true-only behavior.
 * Even when true, full preservation only applies when webview=true is active.
 */
export const PRESERVE_ALL_QUERY_PARAMS = true;

/** True when opened in webview (?webview=true) or that session is still marked. */
export const isWebViewQueryActive = () =>
   new URLSearchParams(window.location.search).get("webview") === "true" ||
   localStorage.getItem("isLoadedOnWebView") === "true";

const getActiveSearchParams = () => {
   const current = window.location.search;
   if (current) return new URLSearchParams(current);

   const stored = localStorage.getItem(WEBVIEW_QUERY_KEY);
   if (stored) return new URLSearchParams(stored.startsWith("?") ? stored : `?${stored}`);

   return new URLSearchParams();
};

/** Native app signal that EU region selection is supported in this webview. */
export const appSupportsEURegion = () => getActiveSearchParams().get("appSupportsEURegion") === "true";

/** Normalize to a leading-? query string, or "". */
const normalizeSearch = (search) => {
   if (!search) return "";
   return search.startsWith("?") ? search : `?${search}`;
};

const searchHasWebView = (search) =>
   new URLSearchParams(normalizeSearch(search)).get("webview") === "true";

/**
 * Full query to preserve across auth redirects — only when webview=true is active.
 * Prefers current URL search (must include webview=true), then stored entry params,
 * then falls back to ?webview=true.
 */
export const getPreservedQueryString = ({ fallbackWebView = false } = {}) => {
   if (!isWebViewQueryActive() && !fallbackWebView) {
      return "";
   }

   const current = normalizeSearch(window.location.search);
   if (current && searchHasWebView(current)) return current;

   const stored = normalizeSearch(localStorage.getItem(WEBVIEW_QUERY_KEY));
   if (stored && searchHasWebView(stored)) return stored;

   return "?webview=true";
};

/** Append preserved query params to a path (strips any existing query on path). */
export const withPreservedQuery = (path, options = {}) => {
   const search = getPreservedQueryString(options);
   const pathname = (path || "/").split("?")[0];
   return search ? `${pathname}${search}` : pathname;
};
